import { postSignout } from 'src/api/auth';
import useUserStore from 'src/hooks/stores/useUserStore';

export const OTP_ROUTE_RELOAD_FLAG = 'uticket:otp-route-active';
export const OTP_INTERACTION_FLAG = 'uticket:otp-interaction-flag';

/**
 * Marca que o usuário interagiu (login/signup) e recebeu otp_pending na aba atual.
 * Deve ser chamado quando o usuário faz login/signup fora do checkout e o modal OTP deve aparecer.
 */
export const markOtpInteraction = (): void => {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		sessionStorage.setItem(OTP_INTERACTION_FLAG, 'true');
	} catch {
		// Ignora erros de storage
	}
};

/**
 * Verifica se o usuário interagiu (login/signup) e recebeu otp_pending na aba atual.
 * Usado para determinar se o modal deve aparecer após reload.
 */
export const hasOtpInteraction = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	try {
		return sessionStorage.getItem(OTP_INTERACTION_FLAG) === 'true';
	} catch {
		return false;
	}
};

/**
 * Remove a flag de interação OTP.
 * Deve ser chamado quando o OTP é validado com sucesso ou quando o modal é fechado.
 */
export const clearOtpInteractionFlag = (): void => {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		sessionStorage.removeItem(OTP_INTERACTION_FLAG);
	} catch {
		// Ignora erros de storage
	}
};

export const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');
  return `${paddedMinutes}:${paddedSeconds}`;
};

export const isReloadNavigation = (): boolean => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return false;
  }
  const navigationEntries = performance.getEntriesByType?.('navigation');
  if (navigationEntries && navigationEntries.length > 0) {
    const [entry] = navigationEntries as PerformanceNavigationTiming[];
    return entry.type === 'reload';
  }
  const legacyPerformance = performance as Performance & {
    navigation?: PerformanceNavigation;
  };
  if (legacyPerformance.navigation) {
    const typeReload =
      typeof legacyPerformance.navigation.TYPE_RELOAD === 'number'
        ? legacyPerformance.navigation.TYPE_RELOAD
        : 1;
    return legacyPerformance.navigation.type === typeReload;
  }
  return false;
};

export const wasOtpRouteActiveBeforeReload = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return sessionStorage.getItem(OTP_ROUTE_RELOAD_FLAG) === 'true';
  } catch {
    return false;
  }
};

export const clearOtpRouteReloadFlag = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(OTP_ROUTE_RELOAD_FLAG);
  } catch {}
};

export const shouldSkipAutoSendOnReload = (
  remaining: number,
  skipAutoSendOnReload: boolean,
): boolean => {
  if (remaining <= 0) return false;
  if (!skipAutoSendOnReload) return false;
  if (!isReloadNavigation()) return false;
  if (!wasOtpRouteActiveBeforeReload()) return false;
  return true;
};

/**
 * Verifica se o erro é do tipo otp_not_required
 * @returns true se o erro é otp_not_required, false caso contrário
 */
export const isOtpNotRequiredError = (error: any): boolean => {
  const status =
    error?.status ??
    error?.data?.status ??
    error?.response?.status ??
    error?.response?.data?.status;

  const code =
    error?.data?.code ??
    error?.response?.data?.code ??
    error?.code;

  return status === 400 && code === 'otp_not_required';
};
export const signoutOtpPendingUser = async (): Promise<void> => {
  const { token, clear } = useUserStore.getState();
  
  if (token) {
    try {
      await postSignout(token);
    } catch {
      // Ignora erro de signout
    }
  }
  
  clear();
};

export const signoutOtpWebUser = signoutOtpPendingUser;